// ASR engine: loads the ONNX model with the chosen backend, runs one
// forward pass per chunk, returns decoded text. All scratch buffers
// (mel input tensor, length tensor, output GPU tensor) are persistent.

import { assert, assertPositiveInt } from "./assert.js";
import { ctcArgmax, ctcMerge } from "./ctc.js";
import { initialBeamState, resetBeamState, ctcBeamStep, ctcBeamFinalize } from "./ctc_beam.js";
import { MelExtractor } from "./mel.js";
import { detokenize } from "./vocab.js";

const AUDIO_SIGNAL = "audio_signal";
const LENGTH = "length";
// MelExtractor reflect-pads the audio by nFft/2 on each side before framing,
// so for a `windowSamples`-long input it emits ~ceil(nFft/hop) more frames
// than the model's expected count (≈3 extra on the default 512/160 config).
// 8 is the next power-of-two margin — enough headroom for plausible config
// drift without growing the scratch beyond a single small allocation.
const MEL_SCRATCH_PAD_FRAMES = 8;
const MS_PER_SEC = 1000;
const WEBGPU = "webgpu";
const WASM = "wasm";

// Not exported: callers go through `createEngine()` (or `createAsr()` higher
// up). Keeping the class file-local means we can change its shape without
// breaking importers.
class Engine {
  constructor(parts) {
    const { ort, session, mel, vocab, backend, expectedFrames, strideSamples } = parts;
    assert(session && typeof session.run === "function", "engine: session missing");
    assert(mel instanceof MelExtractor, "engine: mel must be a MelExtractor");
    assert(Array.isArray(vocab) && vocab.length > 0, "engine: vocab empty");
    assertPositiveInt(expectedFrames, "expectedFrames");
    this.ort = ort;
    this.session = session;
    this.mel = mel;
    this.vocab = vocab;
    this.backend = backend;
    this.expectedFrames = expectedFrames;
    this.logitsName = pickLogitsOutput(session);
    this.blankId = vocab.length; // NeMo CTC convention: blank = vocab_size

    this.modelInput = new Float32Array(mel.nMels * expectedFrames);
    this.melScratch = new Float32Array(mel.nMels * (expectedFrames + MEL_SCRATCH_PAD_FRAMES));
    this.windowSamples = (expectedFrames - 1) * mel.hop + mel.winLen;
    this.audioScratch = new Float32Array(this.windowSamples);
    assertPositiveInt(strideSamples, "strideSamples");
    assert(strideSamples <= this.windowSamples, "stride must be ≤ window");
    this.strideSamples = strideSamples;

    this.inputShape = [1, mel.nMels, expectedFrames];
    this.inputTensor = this._maybeMakeGpuInputTensor() ?? new ort.Tensor(
      "float32",
      this.modelInput,
      this.inputShape,
    );

    // `length` is baked into the model as a constant initializer when present
    // in `inputNames`. Pass a length tensor only if the model still asks for it.
    // NOTE: these persistent feeds only serve the WebGPU branch of _buildFeeds();
    // on WASM every run builds fresh feeds (proxy transfer detaches buffers), so
    // there this is one small dead allocation kept for the WebGPU path.
    this.feeds = { [AUDIO_SIGNAL]: this.inputTensor };
    this._needsLength = session.inputNames.includes(LENGTH);
    if (this._needsLength) {
      const lengthData = new BigInt64Array([BigInt(expectedFrames)]);
      this.feeds[LENGTH] = new ort.Tensor("int64", lengthData, [1]);
    }
    this.fetches = { [this.logitsName]: null };
    // Serialize concurrent transcribe() callers. Every scratch buffer
    // (modelInput, melScratch, audioScratch, padded) and the input tensor
    // itself is shared, so two interleaved runs would clobber each other
    // across the session.run await point. UI code commonly fires transcribe
    // from event handlers without awaiting — single-flight makes that safe.
    this._tail = Promise.resolve();
    this._disposed = false;
    // Pooled beam state reused across _transcribeSerialBeam() calls (lazy).
    this._beamScratch = null;
  }

  // Allocate a persistent GPUBuffer for the input tensor so that each
  // transcribe() only does a single small writeBuffer instead of letting
  // ORT stage a CPU→GPU upload through its own intermediate buffer.
  // Returns null on the WASM backend or if env.webgpu.device isn't ready.
  _maybeMakeGpuInputTensor() {
    if (this.backend !== WEBGPU) return null;
    const device = this.ort.env.webgpu?.device;
    if (!device) {
      // WebGPU EP loaded the model but didn't publish its device handle yet
      // (usually a session-init race). We can still run — ORT will stage a
      // CPU→GPU copy per inference — but this is a real perf regression
      // (~hundreds of µs / call on iPad 9). Surface it loudly so anyone
      // debugging "why is WebGPU not faster than WASM" has a thread to pull.
      console.warn(
        "[asr] WebGPU backend selected but ort.env.webgpu.device is not ready; " +
        "input tensor will live on CPU and ORT will stage a copy per inference.",
      );
      return null;
    }
    const byteLength = this.modelInput.byteLength;
    this.gpuInputBuffer = device.createBuffer({
      size: byteLength,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      mappedAtCreation: false,
    });
    this.gpuDevice = device;
    return this.ort.Tensor.fromGpuBuffer(this.gpuInputBuffer, {
      dataType: "float32",
      dims: this.inputShape,
    });
  }

  // Input feeds for one session.run(). On WebGPU the input is persistent (it
  // lives in a GPUBuffer refreshed via writeBuffer), so reuse this.feeds. On the
  // WASM path we build a fresh input tensor per run: with ort.env.wasm.proxy =
  // true, ORT transfers (detaches) input buffers into its worker, so a single
  // persistent tensor backed by this.modelInput would be neutered after the
  // first run — every later repackMel would write into a dead buffer. The copy
  // is ~nMels×frames floats (~32 KB), an order of magnitude below the per-run
  // logits copy removed in _transcribeOneLogits, so the run is still a net win.
  // (Correct under proxy:false too — it just costs one small copy.)
  _buildFeeds() {
    // Gate on the backend, not gpuDevice: on WebGPU-without-device (session-init
    // race) the persistent CPU tensor is still safe to reuse — the WebGPU EP
    // ignores ort.env.wasm.proxy, so nothing detaches its buffer.
    if (this.backend === WEBGPU) return this.feeds;
    const feeds = {
      [AUDIO_SIGNAL]: new this.ort.Tensor("float32", this.modelInput.slice(), this.inputShape),
    };
    if (this._needsLength) {
      feeds[LENGTH] = new this.ort.Tensor("int64", BigInt64Array.of(BigInt(this.expectedFrames)), [1]);
    }
    return feeds;
  }

  _window(audio) {
    const want = this.windowSamples;
    if (audio.length === want) return audio;
    if (audio.length > want) return audio.subarray(0, want);
    // audio.length < want: copy what we have and zero-pad the tail.
    const buf = this.audioScratch;
    buf.set(audio, 0);
    for (let i = audio.length; i < want; i += 1) buf[i] = 0;
    return buf;
  }

  // Chunked CTC stitching (after NeMo's FrameBatchASR):
  //   - run a sliding window of size `windowSamples` advancing by `strideSamples`
  //   - for each window, take the model's per-frame argmax (BEFORE the repeat-
  //     collapse pass), keep only the "interior" portion of the argmax
  //     sequence (the middle of the overlap on each side belongs to a
  //     neighboring window), append to a global argmax stream
  //   - finally run a single ctcMerge() over the whole stitched stream so
  //     repeats that span chunk boundaries collapse correctly
  //
  // For audio shorter than `windowSamples` this reduces to one window.
  // `audioOrThunk` is either a Float32Array of PCM samples or a thunk that
  // returns one (sync or async). The thunk form lets callers defer expensive
  // decode work (decodeAudioData, OfflineAudioContext.startRendering, ...)
  // until they reach the head of the serialized queue, so four concurrent
  // calls don't end up spinning up four AudioContexts in parallel.
  // beamOpts: optional { beamWidth, topK, trie, vocab, lmAlpha, lmBeta }.
  // When present, logits are stitched across chunks and decoded with CTC beam
  // search instead of per-chunk argmax. When absent, falls back to argmax.
  transcribe(audioOrThunk, beamOpts) {
    if (this._disposed) {
      return Promise.reject(new Error("engine: transcribe() called after dispose()"));
    }
    const next = this._tail.then(async () => {
      const audio = typeof audioOrThunk === "function"
        ? await audioOrThunk()
        : audioOrThunk;
      return beamOpts
        ? this._transcribeSerialBeam(audio, beamOpts)
        : this._transcribeSerial(audio);
    });
    // Swallow rejections in the chain so one failed call doesn't poison
    // subsequent ones; the original promise still rejects to the caller.
    this._tail = next.catch(() => undefined);
    return next;
  }

  async _transcribeSerial(audio) {
    const t0 = performance.now();
    const W = this.windowSamples;
    const S = this.strideSamples;
    const stitched = [];
    let melMs = 0;
    let inferMs = 0;
    let decodeMs = 0;

    // Walk windows; offsets are in samples.
    // Run at least one window even when audio is empty (matches single-shot semantics).
    const total = Math.max(audio.length, 1);
    for (let off = 0; off < total; off += S) {
      const chunk = audio.subarray(off, off + W);
      const r = await this._transcribeOne(chunk);
      const slice = this._sliceForStitch(r.argmax.length, off, audio.length, W, S);
      for (let i = slice.start; i < slice.end; i += 1) stitched.push(r.argmax[i]);
      melMs += r.mel;
      inferMs += r.inference;
      decodeMs += r.decode;
      if (off + W >= audio.length) break;
    }

    const merged = ctcMerge(stitched, this.blankId);
    const text = detokenize(this.vocab, merged);
    return {
      text,
      timings: {
        mel: melMs,
        inference: inferMs,
        decode: decodeMs,
        total: performance.now() - t0,
      },
    };
  }

  // Beam-search variant of _transcribeSerial.
  //
  // Threads beam state across chunks rather than accumulating logits, so memory
  // stays O(one chunk) regardless of audio length. Each chunk's owned logit
  // slice (same middle-of-overlap bounds as argmax stitching) is processed and
  // then released — peak footprint is one Float32Array of ~415 KB.
  //
  // The threaded `beams` state object carries the decoder's pooled scratch and
  // node/cell pools, so they're allocated once and reused across chunks; beamOpts
  // is read-only config.
  async _transcribeSerialBeam(audio, beamOpts) {
    const t0 = performance.now();
    const W = this.windowSamples;
    const S = this.strideSamples;
    const V = this.blankId + 1;
    let melMs = 0;
    let inferMs = 0;
    let decodeMs = 0;

    // Reuse one pooled beam state across calls. transcribe() is invoked per
    // rolling-window stride (~5 Hz) by the realtime path; allocating a fresh
    // initialBeamState() each call churns the SoA pools. Calls are serialized by
    // _tail and the result is fully read (finalize copies tokens/scores out)
    // before the next call, so resetting the same scratch is safe and decodes
    // identically. Each call resets to the empty hypothesis for this window.
    let beams = this._beamScratch
      ? resetBeamState(this._beamScratch, beamOpts.trie ?? null)
      : (this._beamScratch = initialBeamState(beamOpts.trie ?? null));

    const total = Math.max(audio.length, 1);
    for (let off = 0; off < total; off += S) {
      const chunk = audio.subarray(off, off + W);
      const r = await this._transcribeOneLogits(chunk);
      const slice = this._sliceForStitch(r.timeSteps, off, audio.length, W, S);
      const nFrames = slice.end - slice.start;

      const tDecode = performance.now();
      // Process only the owned rows, synchronously, then release the live view
      // before the next iteration's session.run() can recycle ORT's buffer.
      // dispose() in finally: the view is a live ORT tensor (not a GC-able
      // copy), so a decoder throw must not leak it.
      try {
        beams = ctcBeamStep(
          r.logits.subarray(slice.start * V, slice.end * V),
          nFrames, V, this.blankId, beams, beamOpts,
        );
      } finally {
        r.dispose();
      }
      decodeMs += performance.now() - tDecode;
      melMs    += r.mel;
      inferMs  += r.inference;
      if (off + W >= audio.length) break;
    }

    const tDecode = performance.now();
    const { tokens, wordScores } = ctcBeamFinalize(beams);
    const text   = detokenize(beamOpts.vocab ?? this.vocab, tokens);
    return {
      text,
      wordScores,
      timings: {
        mel:       melMs,
        inference: inferMs,
        decode:    decodeMs + (performance.now() - tDecode),
        total:     performance.now() - t0,
      },
    };
  }

  // Like _transcribeOne but returns the raw logits (for CTC beam search) instead
  // of the argmax token sequence. Used by _transcribeSerialBeam and createSession.
  //
  // Zero-copy: `logits` is the LIVE view into ORT's output buffer, not a copy.
  // Under proxy:false ORT may recycle that buffer on the next session.run()
  // (mem pattern / arena); under proxy:true the view is a freshly-transferred
  // owned buffer, so the recycling hazard is really proxy:false's — which is
  // exactly the P3 revert path, so the contract must hold in both regimes:
  // the caller must consume `logits` synchronously (ctcBeamStep reads every
  // frame in a tight loop, no awaits) and then call `dispose()` BEFORE the
  // next run() — and must not retain the view past that.
  // Skipping the copy saves a ~timeSteps×V Float32Array (~415 KB/window on the
  // production model) per chunk; on the 5 Hz rolling-window path that is ~2 MB/s
  // of avoided GC.
  async _transcribeOneLogits(audio) {
    const tMel = performance.now();
    const windowed = this._window(audio);
    const mel = this.mel.extract(windowed, this.melScratch);
    repackMel(mel.data, this.modelInput, this.mel.nMels, mel.nFrames, this.expectedFrames);
    if (this.gpuDevice) {
      this.gpuDevice.queue.writeBuffer(
        this.gpuInputBuffer, 0,
        this.modelInput.buffer, this.modelInput.byteOffset, this.modelInput.byteLength,
      );
    }
    const tInfer = performance.now();
    const out = await this.session.run(this._buildFeeds(), this.fetches);
    const logitsTensor = out[this.logitsName];
    const [, timeSteps] = logitsTensor.dims;
    return {
      logits: logitsTensor.data,
      timeSteps,
      dispose: () => logitsTensor.dispose?.(),
      mel: tInfer - tMel,
      inference: performance.now() - tInfer,
    };
  }

  // Decide which argmax tokens of this window contribute to the global
  // sequence. NeMo's middle-of-overlap rule: each window owns the audio
  // strictly between (a) the midpoint of the overlap with the previous
  // window and (b) the midpoint of the overlap with the next window. In
  // token space those two midpoints — measured in this window's local
  // frame — are leftMid = T*(1-S/W)/2 and rightMid = T*(1+S/W)/2.
  // Edge windows extend to the graph boundary; the last window also
  // clips at the actual audio end.
  _sliceForStitch(T, audioOff, audioLen, W, S) {
    const half = (T * S) / (2 * W);
    const leftMid = Math.round(T / 2 - half);
    const rightMid = Math.round(T / 2 + half);
    const isFirst = audioOff === 0;
    const isLast = audioOff + W >= audioLen;
    const start = isFirst ? 0 : leftMid;
    let end = isLast ? T : rightMid;
    if (isLast) {
      const samplesPerToken = W / T;
      const realSamples = Math.max(0, audioLen - audioOff);
      const realTokens = Math.ceil(realSamples / samplesPerToken);
      if (realTokens < end) end = realTokens;
    }
    return { start, end: Math.max(start, end) };
  }

  // Streaming session for microphone input.  The caller owns chunking —
  // each processChunk() call accepts exactly one window of PCM audio
  // (windowSamples long) and advances the beam state by those frames.
  // No stitching: chunks are non-overlapping so all output frames are used.
  createSession(beamOpts) {
    if (this._disposed) throw new Error("engine: createSession() called after dispose()");
    const V = this.blankId + 1;
    let beams = initialBeamState(beamOpts?.trie ?? null);

    const processChunk = (pcm) => {
      if (this._disposed) {
        return Promise.reject(new Error("engine: session used after dispose()"));
      }
      const next = this._tail.then(async () => {
        const r = await this._transcribeOneLogits(pcm);
        const tDecode = performance.now();
        // Consume the live logits view synchronously, then release it — in
        // finally, so a decoder throw can't leak the live ORT tensor.
        try {
          beams = ctcBeamStep(r.logits, r.timeSteps, V, this.blankId, beams, beamOpts ?? {});
        } finally {
          r.dispose();
        }
        const { tokens, wordScores } = ctcBeamFinalize(beams);
        const text   = detokenize((beamOpts?.vocab) ?? this.vocab, tokens);
        return {
          text,
          wordScores,
          timings: {
            mel:       r.mel,
            inference: r.inference,
            decode:    performance.now() - tDecode,
          },
        };
      });
      this._tail = next.catch(() => undefined);
      return next;
    };

    const finalize = () => {
      const { tokens, wordScores } = ctcBeamFinalize(beams);
      return { text: detokenize((beamOpts?.vocab) ?? this.vocab, tokens), wordScores };
    };

    return { processChunk, finalize };
  }

  async dispose() {
    // Flip the flag before awaiting so any transcribe() called between now
    // and the next microtask gets a clean error rather than queueing behind
    // a session we're about to release.
    this._disposed = true;
    // Drain the in-flight queue before releasing anything. Destroying the
    // GPUBuffer while session.run is still reading it would either crash
    // or silently produce garbage on the final pending result.
    await this._tail;
    try {
      // Order matters: release the session first so ORT-Web doesn't hold an
      // outstanding reference to the GPUBuffer when we destroy it.
      await this.session.release?.();
    } finally {
      // Destroy the buffer even if release() rejected — otherwise the GPU
      // allocation leaks for the lifetime of the page.
      this.gpuInputBuffer?.destroy();
      this.gpuInputBuffer = null;
      this.gpuDevice = null;
    }
  }

  async _transcribeOne(audio) {
    const tMel = performance.now();
    const windowed = this._window(audio);
    const mel = this.mel.extract(windowed, this.melScratch);
    repackMel(mel.data, this.modelInput, this.mel.nMels, mel.nFrames, this.expectedFrames);
    if (this.gpuDevice) {
      this.gpuDevice.queue.writeBuffer(this.gpuInputBuffer, 0, this.modelInput.buffer, this.modelInput.byteOffset, this.modelInput.byteLength);
    }
    const tInfer = performance.now();
    const out = await this.session.run(this._buildFeeds(), this.fetches);
    const tDecode = performance.now();
    const logits = out[this.logitsName];
    const [, timeSteps, vocabSize] = logits.dims;
    const argmax = ctcArgmax(logits.data, timeSteps, vocabSize);
    logits.dispose?.();
    const tEnd = performance.now();
    return { argmax, mel: tInfer - tMel, inference: tDecode - tInfer, decode: tEnd - tDecode };
  }
}

function repackMel(src, dst, nMels, srcFrames, dstFrames) {
  const copy = Math.min(srcFrames, dstFrames);
  for (let m = 0; m < nMels; m += 1) {
    const srcBase = m * srcFrames;
    const dstBase = m * dstFrames;
    for (let f = 0; f < copy; f += 1) dst[dstBase + f] = src[srcBase + f];
    for (let f = copy; f < dstFrames; f += 1) dst[dstBase + f] = 0;
  }
}

function pickLogitsOutput(session) {
  const meta = session.outputMetadata || {};
  const threeD = session.outputNames.filter((name) => {
    const d = meta[name]?.dimensions;
    return Array.isArray(d) && d.length === 3;
  });
  if (threeD.length === 1) return threeD[0];
  // No 3-D shape metadata at all (older exports skip outputMetadata): fall
  // back to the single output if there's only one, else refuse to guess.
  if (threeD.length === 0 && session.outputNames.length === 1) {
    return session.outputNames[0];
  }
  throw new Error(
    `engine: expected exactly one 3-D model output, found ${threeD.length} ` +
    `candidate(s): [${threeD.join(", ") || "none"}] in [${session.outputNames.join(", ")}]. ` +
    "If the model export changed (encoder states, aux heads, ...) update pickLogitsOutput().",
  );
}

export async function createEngine(params) {
  const { ort, modelUrl, cfg, vocab, backend, strideMs } = params;
  assertPositiveInt(cfg?.n_mel_frames, "cfg.n_mel_frames");
  const opts = sessionOptionsFor(backend);
  const session = await ort.InferenceSession.create(modelUrl, opts);
  const windowSamples = (cfg.n_mel_frames - 1) * cfg.hop_length + cfg.win_length;
  const windowMs = (windowSamples * MS_PER_SEC) / cfg.sample_rate;
  const effectiveStrideMs = strideMs ?? windowMs;
  if (effectiveStrideMs <= 0 || effectiveStrideMs > windowMs) {
    throw new Error(`strideMs must be in (0, ${windowMs.toFixed(1)}], got ${effectiveStrideMs}`);
  }
  const strideSamples = Math.round((effectiveStrideMs / MS_PER_SEC) * cfg.sample_rate);
  const mel = new MelExtractor(cfg, windowSamples);
  return new Engine({
    ort,
    session,
    mel,
    vocab,
    backend,
    expectedFrames: cfg.n_mel_frames,
    strideSamples,
  });
}

// Audited: ORT logs "Some nodes were not assigned to the preferred EP" at WARN
// level because shape ops are explicitly pinned to CPU by ORT itself, by design.
// We raise the session threshold to ERROR for this known, intentional behavior.
// Real defects still surface at error/fatal severity.
const SESSION_LOG_SEVERITY_ERROR = 3;

const SESSION_OPTS = Object.freeze({
  [WEBGPU]: {
    executionProviders: [WEBGPU],
    graphOptimizationLevel: "all",
    enableMemPattern: false,
    enableCpuMemArena: false,
    preferredOutputLocation: "cpu",
    logSeverityLevel: SESSION_LOG_SEVERITY_ERROR,
  },
  [WASM]: {
    executionProviders: [WASM],
    graphOptimizationLevel: "all",
    enableMemPattern: true,
    enableCpuMemArena: true,
    logSeverityLevel: SESSION_LOG_SEVERITY_ERROR,
  },
});

function sessionOptionsFor(backend) {
  const opts = SESSION_OPTS[backend];
  if (!opts) throw new Error(`No session options registered for backend "${backend}"`);
  return opts;
}
